import React, { useCallback, useEffect, useState } from 'react';
import {
  SettingsAddress,
  ShippingSettings,
  emptyAddress,
  getSettings,
  saveShippingSettings,
} from '../api/settings';
import { borderRadius, colors, spacing, typography } from '../styles/tokens';

const AddressFields: React.FC<{
  value: SettingsAddress;
  onChange: (next: SettingsAddress) => void;
  idPrefix: string;
}> = ({ value, onChange, idPrefix }) => {
  const set = (key: keyof SettingsAddress, raw: string) => {
    onChange({ ...value, [key]: raw });
  };

  const field = (
    label: string,
    key: keyof SettingsAddress,
    opts?: { wide?: boolean; maxLength?: number },
  ) => (
    <label
      style={{ ...styles.field, ...(opts?.wide ? { gridColumn: '1 / -1' } : {}) }}
      htmlFor={`${idPrefix}-${key}`}
    >
      <span style={styles.label}>{label}</span>
      <input
        id={`${idPrefix}-${key}`}
        value={value[key]}
        maxLength={opts?.maxLength}
        onChange={(e) => set(key, key === 'state' ? e.target.value.toUpperCase() : e.target.value)}
        style={styles.input}
      />
    </label>
  );

  return (
    <div style={styles.addressGrid}>
      {field('Name', 'name', { wide: true })}
      {field('Street', 'street1', { wide: true })}
      {field('Street 2', 'street2', { wide: true })}
      {field('City', 'city')}
      {field('State', 'state', { maxLength: 2 })}
      {field('ZIP', 'zip')}
      {field('Country', 'country', { maxLength: 2 })}
    </div>
  );
};

export const SettingsPage: React.FC = () => {
  const [shipping, setShipping] = useState<ShippingSettings | null>(null);
  const [buylistShipTo, setBuylistShipTo] = useState<SettingsAddress>(emptyAddress());
  const [accountAddress, setAccountAddress] = useState<SettingsAddress>(emptyAddress());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getSettings();
      setShipping(data.shipping);
      setBuylistShipTo({ ...emptyAddress(), ...data.shipping.buylistShipTo });
      setAccountAddress({ ...emptyAddress(), ...data.shipping.accountAddress });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const onSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);
    setError(null);
    try {
      const result = await saveShippingSettings({ buylistShipTo, accountAddress });
      setShipping(result.shipping);
      setMessage('Shipping settings saved.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={styles.page}>
      <h1 style={typography.h1}>Settings</h1>
      <p style={styles.lede}>
        Store configuration edited here. Secrets (API keys, SMTP passwords) stay in environment /
        Vault.
      </p>

      {loading && <p>Loading…</p>}
      {error && <p style={styles.error}>{error}</p>}
      {message && <p style={styles.success}>{message}</p>}

      {!loading && shipping && (
        <form onSubmit={(e) => void onSave(e)} style={styles.section}>
          <h2 style={typography.h2}>Shipping destinations</h2>
          <p style={styles.hint}>
            Used when buying prepaid buylist labels. USPS uses the buylist ship-to (PO Box OK). UPS
            uses the account / street address (PO Box not allowed).
          </p>

          <div style={styles.card}>
            <div style={styles.cardHeader}>
              <h3 style={typography.h3}>Buylist ship-to (USPS)</h3>
              <span style={{ ...typography.small, color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                source: {shipping.buylistShipToSource}
                {!shipping.buylistShipToComplete ? ' · incomplete' : ''}
              </span>
            </div>
            <AddressFields
              idPrefix="buylist"
              value={buylistShipTo}
              onChange={setBuylistShipTo}
            />
          </div>

          <div style={styles.card}>
            <div style={styles.cardHeader}>
              <h3 style={typography.h3}>Account address (UPS)</h3>
              <span style={{ ...typography.small, color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                source: {shipping.accountAddressSource}
                {!shipping.accountAddressComplete ? ' · incomplete' : ''}
              </span>
            </div>
            <AddressFields
              idPrefix="account"
              value={accountAddress}
              onChange={setAccountAddress}
            />
          </div>

          <button type="submit" disabled={saving} style={styles.saveBtn}>
            {saving ? 'Saving…' : 'Save shipping settings'}
          </button>
        </form>
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  page: {
    padding: spacing.lg,
    maxWidth: 880,
  },
  lede: {
    ...typography.body,
    color: colors.textSecondary,
    marginTop: spacing.sm,
    marginBottom: spacing.lg,
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.md,
  },
  hint: {
    ...typography.small,
    color: colors.textSecondary,
    margin: 0,
  },
  card: {
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    padding: spacing.md,
    background: colors.surface,
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: spacing.sm,
    marginBottom: spacing.md,
  },
  addressGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
    gap: spacing.sm,
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.xs,
  },
  label: {
    ...typography.small,
    color: colors.textSecondary,
  },
  input: {
    padding: spacing.sm,
    borderRadius: borderRadius.sm,
    border: `1px solid ${colors.border}`,
    font: 'inherit',
  },
  saveBtn: {
    alignSelf: 'flex-start',
    padding: `${spacing.sm} ${spacing.md}`,
    borderRadius: borderRadius.sm,
    border: 'none',
    background: colors.primary,
    color: '#fff',
    cursor: 'pointer',
    fontWeight: 600,
  },
  error: { color: colors.error },
  success: { color: colors.success },
};

export default SettingsPage;
